import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, HelpCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import StructuredData from '../../components/StructuredData';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const SITE = 'https://staymii.com';
const PAGE = '/integrations/beds24-e-invoice/faq';

const faqs = [
  { q: 'What is the StayMii Beds24 e-Invoice integration?', a: 'It connects your Beds24 property management system to Malaysia\u2019s LHDN MyInvois platform, automatically converting every Beds24 booking into a compliant e-invoice and submitting it to LHDN — with no manual data entry.' },
  { q: 'When does the Beds24 e-Invoice integration launch in Malaysia?', a: 'It is officially live from 23 October 2026, following a soft launch on 16 July 2026. You can get started now with a 14-day free trial and no credit card required.' },
  { q: 'Which OTA channels does it cover?', a: 'Any booking that reaches Beds24 — Booking.com, Agoda, Expedia, Airbnb, or direct — is captured and turned into an LHDN e-invoice.' },
  { q: 'Do I need to migrate away from Beds24?', a: 'No. You continue using Beds24 as normal. StayMii connects through the Beds24 API using a key you generate; there is no migration and no disruption.' },
  { q: 'How does it handle SST, service charge and tourism tax?', a: 'StayMii itemises 8% SST, 10% service charge and RM10/room/night tourism tax (for foreign guests) as separate lines, exactly as LHDN requires for hotels and short-term rentals.' },
  { q: 'What about cancellations and rate changes?', a: 'Cancellations and rate adjustments in Beds24 automatically generate the matching Credit Note or Debit Note referencing the original invoice UUID.' },
  { q: 'Can I issue e-invoices for past Beds24 bookings?', a: 'Yes. StayMii can backfill recent Beds24 bookings so you can issue e-invoices retroactively to cover the mandate period.' },
  { q: 'What do foreign guests without a Malaysian TIN require?', a: 'For guests without a Malaysian TIN, StayMii uses LHDN\u2019s general public TIN (EI00000000010), or you can collect the passport number via a digital check-in form.' },
  { q: 'How long does setup take?', a: 'Under 15 minutes: generate a Beds24 API key, connect it in StayMii, add your MyInvois credentials, test in sandbox, then go live.' },
  { q: 'How much does the Beds24 e-Invoice plugin cost?', a: 'It starts with a 14-day free trial (no card required). Paid plans scale by rooms and properties. Request a demo for pricing tailored to your portfolio.' },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Beds24 e-Invoice Malaysia', item: SITE + '/integrations/beds24-e-invoice' },
    { '@type': 'ListItem', position: 3, name: 'FAQ', item: SITE + PAGE },
  ],
};

export default function Beds24EInvoiceFAQ() {
  return (
    <div className="min-h-screen bg-white" data-testid="beds24-einvoice-faq-page">
      <SEO
        title="Beds24 e-Invoice Malaysia FAQ — LHDN MyInvois Integration Questions"
        description="Answers to common questions about the StayMii Beds24 e-Invoice integration for Malaysia: setup, SST & tourism tax, MyInvois submission, pricing and the 23 October 2026 official launch."
        keywords="beds24 e-invoice malaysia, beds24 myinvois integration faq, lhdn e-invoice for beds24, hotel e-invoice malaysia"
        canonicalUrl={PAGE}
      />
      <StructuredData id="beds24-faq" schema={[faqSchema, breadcrumbSchema]} />
      <LandingNavBar />

      <section className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <Link to="/integrations/beds24-e-invoice" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Beds24 e-Invoice Malaysia</Link>
        <div className="flex items-center gap-2 mb-3"><HelpCircle className="w-6 h-6 text-blue-600" /><h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900">Beds24 e-Invoice Malaysia — FAQ</h1></div>
        <p className="text-slate-500 mb-10">Everything Malaysian Beds24 operators ask about LHDN MyInvois e-invoicing with StayMii.</p>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <details key={i} className="group rounded-xl border border-slate-200 p-5" data-testid={`beds24-faq-item-${i}`}>
              <summary className="font-semibold text-slate-900 cursor-pointer list-none flex justify-between items-center gap-4">{f.q}<span className="text-blue-600 text-xl leading-none group-open:rotate-45 transition-transform">+</span></summary>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-10 p-6 rounded-2xl bg-[#0B1120] text-center">
          <p className="text-xl font-bold text-white">Still have questions about Beds24 e-Invoice?</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold">Request a Demo <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/auth/signup" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/20">Get Started</Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
